import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COPY = {
  "pre-approval": {
    title: "Get Pre-Approved",
    desc: "Share a few details and Akashdeep will begin your personalized pre-approval strategy.",
    cta: "START MY PRE-APPROVAL",
  },
  consultation: {
    title: "Book a Consultation",
    desc: "Request a private mortgage strategy consultation with Akashdeep. No obligation.",
    cta: "REQUEST CONSULTATION",
  },
};

const GOALS = ["First Home", "Move-Up", "Refinance", "Investment", "Self-Employed", "Other"];
const TIMELINES = ["0–3 Months", "3–6 Months", "6–12 Months", "Just Exploring"];

const EMPTY = {
  name: "",
  email: "",
  phone: "",
  mortgage_goal: "",
  purchase_price: "",
  down_payment: "",
  timeline: "",
  message: "",
};

function PillGroup({ options, value, onChange, testId }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={testId}>
      {options.map((o) => (
        <button
          key={o}
          type="button"
          data-testid={`${testId}-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          onClick={() => onChange(o)}
          className={`px-3.5 py-2 text-[11px] tracking-wide rounded-sm border transition-colors duration-300 ${
            value === o
              ? "border-gold/70 text-gold bg-gold/10"
              : "border-white/15 text-ivory/55 hover:border-gold/40 hover:text-ivory"
          }`}
        >
          {o}
        </button>
      ))}
    </div>
  );
}

export default function LeadModal({ intent, onClose }) {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const copy = COPY[intent] || COPY.consultation;

  useEffect(() => {
    if (intent) setForm(EMPTY);
  }, [intent]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target ? e.target.value : e }));

  const submit = async (ev) => {
    ev.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please provide your name and email.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/leads`, { ...form, intent });
      toast.success("Your request has been received. Akashdeep will be in touch soon.");
      onClose();
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const field =
    "bg-white/5 border-white/15 text-ivory placeholder:text-ivory/30 focus-visible:ring-gold/60 h-11 rounded-sm";

  return (
    <Dialog open={!!intent} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        data-testid="lead-modal"
        data-lenis-prevent
        className="bg-charcoal-800 border border-gold/25 text-ivory rounded-md sm:max-w-md backdrop-blur-2xl max-h-[88vh] overflow-y-auto"
      >
        <DialogHeader>
          <p className="gold-label mb-2">Akashdeep Ghai · Mortgage Agent</p>
          <DialogTitle data-testid="lead-modal-title" className="font-serif text-2xl text-ivory">{copy.title}</DialogTitle>
          <DialogDescription className="text-ivory/50 font-light">{copy.desc}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5 mt-2">
          <Input data-testid="lead-name-input" placeholder="Full name" value={form.name} onChange={set("name")} className={field} />
          <Input data-testid="lead-email-input" type="email" placeholder="Email address" value={form.email} onChange={set("email")} className={field} />
          <Input data-testid="lead-phone-input" type="tel" placeholder="Phone" value={form.phone} onChange={set("phone")} className={field} />
          <div>
            <label className="text-[11px] uppercase tracking-[0.2em] text-ivory/50 block mb-2.5">Mortgage Goal</label>
            <PillGroup options={GOALS} value={form.mortgage_goal} onChange={set("mortgage_goal")} testId="lead-goal" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-[0.2em] text-ivory/50 block mb-2.5">Est. Purchase Price</label>
              <Input data-testid="lead-price-input" type="number" min="0" placeholder="$ 750,000" value={form.purchase_price} onChange={set("purchase_price")} className={field} />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.2em] text-ivory/50 block mb-2.5">Down Payment</label>
              <Input data-testid="lead-downpayment-input" type="number" min="0" placeholder="$ 150,000" value={form.down_payment} onChange={set("down_payment")} className={field} />
            </div>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.2em] text-ivory/50 block mb-2.5">Timeline</label>
            <PillGroup options={TIMELINES} value={form.timeline} onChange={set("timeline")} testId="lead-timeline" />
          </div>
          <textarea
            data-testid="lead-message-input"
            placeholder="Message (optional)"
            value={form.message}
            onChange={set("message")}
            rows={3}
            className="w-full bg-white/5 border border-white/15 text-ivory placeholder:text-ivory/30 rounded-sm px-3 py-2.5 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gold/60 resize-none"
          />
          <button
            data-testid="lead-submit-btn"
            type="submit"
            disabled={sending}
            className="w-full px-6 py-4 text-xs tracking-[0.2em] font-semibold text-charcoal bg-gold hover:bg-champagne transition-colors duration-300 rounded-sm disabled:opacity-60"
          >
            {sending ? "SENDING…" : copy.cta}
          </button>
          <p className="text-[10px] text-ivory/30 leading-relaxed text-center">
            Your information is kept confidential and never shared without consent.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
